import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone, CheckCircle, UserCheck, Zap, Clock, Sparkles, ShieldCheck, Home, Trees, Scissors } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CostSimulator from '../components/CostSimulator';
import { SEOHead, localBusinessSchema, faqSchema } from '../components/SEOHead';
import { SERVICES } from '../lib/services-data';
import { AGENCIES } from '../lib/agencies-data';
import { getNearbyCities, cityPath, SAP_NUMBER } from '../lib/cities-data';
import { useCity } from '../hooks/useCity';
import { CityPageSkeleton } from '../components/CitySearch';
import { serviceCityPath } from '../lib/service-city-data';
import { COMPANY_INFO } from '../lib/utils';
import { SeasonalLocalContent } from '../components/SeasonalLocalContent';
import { VIEWPORT_ONCE } from '../lib/motion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const HIGHLIGHT_IDS = ['entretien-menager', 'espaces-verts', 'entretien-linge', 'nettoyage-vitres', 'petit-bricolage', 'courses-sorties'];

export default function CityPage() {
  const { citySlug } = useParams();
  const { city, loading } = useCity(citySlug);
  const agency = AGENCIES['artois.ptitservices'];

  if (loading) return <CityPageSkeleton />;
  if (!city) return <Navigate to="/services-a-domicile" replace />;

  const nearby = getNearbyCities(city);
  const services = SERVICES.filter((s) => HIGHLIGHT_IDS.includes(s.id));
  const globals = SERVICES.filter((s) => s.isGlobal);

  const faq = [
    { q: `Intervenez-vous vraiment à ${city.name} ?`, a: `Oui. ${city.name} (${city.region}) est dans notre zone d'intervention de 40 km autour de notre agence de Servins. Un Easer dédié vous est attribué et intervient selon le planning défini ensemble.` },
    { q: `Combien coûte une femme de ménage à ${city.name} ?`, a: `Notre tarif ménage est de 29,99 €/h, soit 14,99 €/h après le crédit d'impôt de 50% avec avance immédiate. Pour le jardinage : 49,90 €/h, soit 24,95 €/h. Tarifs « à partir de », devis gratuit et sans engagement.` },
    { q: `Comment fonctionne l'avance immédiate à ${city.name} ?`, a: `Nous sommes déclarés Services à la Personne (${SAP_NUMBER}). Lors de notre rendez-vous, nous vous inscrivons à l'avance immédiate Urssaf : vous ne payez que 50% de chaque facture, sans attendre votre déclaration d'impôts.` },
    { q: `Sous quel délai pouvez-vous intervenir à ${city.name} ?`, a: `Nous vous recontactons sous 24h ouvrées et fixons un rendez-vous d'évaluation gratuit à votre domicile. Les premières interventions démarrent généralement sous une à deux semaines.` },
  ];

  const title = `Services à domicile à ${city.name}`;
  const description = `Femme de ménage, repassage, vitres, jardinage et petit bricolage à ${city.name}. Crédit d'impôt 50% avec avance immédiate : ménage dès 14,99 €/h, jardinage dès 24,95 €/h. Easer dédié, devis gratuit sous 24h.`;

  const schema = {
    ...localBusinessSchema(agency),
    "@id": `https://ptitservices.com${cityPath(city)}`,
    "url": `https://ptitservices.com${cityPath(city)}`,
    "name": `Ptitservices.com — Services à domicile à ${city.name}`,
    "areaServed": { "@type": "City", "name": city.name, "address": { "@type": "PostalAddress", "addressLocality": city.name, "postalCode": city.cp || undefined, "addressCountry": "FR" } },
  };

  return (
    <div data-testid="city-page">
      <SEOHead title={title} description={description} path={cityPath(city)} schema={[schema, faqSchema(faq)]} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <Link to="/services-a-domicile" className="hover:text-foreground">Villes desservies</Link>
            <span>/</span>
            <span className="text-foreground font-medium">{city.name}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp}>
                <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
                  <MapPin className="h-4 w-4 mr-2" /> {city.region}
                </Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="city-page-title">
                Ménage & jardinage à domicile à <span className="text-[hsl(var(--primary))]">{city.name}</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                {city.intro}
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Ménage à {city.name} — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">29,99 €/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">14,99 €/h</span>
                </div>
                <div className="px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <span className="text-xs text-muted-foreground block">Jardinage à {city.name} — à partir de</span>
                  <span className="text-sm line-through text-muted-foreground">49,90 €/h</span>
                  <span className="text-lg font-bold text-[hsl(var(--primary))] ml-2">24,95 €/h</span>
                </div>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/devis" state={{ ville: city.name, cp: city.cp || '' }}>
                  <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg" data-testid="city-quote-button">
                    Devis gratuit à {city.name}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                  <Button size="lg" variant="outline" data-testid="city-phone-button">
                    <Phone className="mr-2 h-4 w-4" /> {COMPANY_INFO.phone}
                  </Button>
                </a>
              </motion.div>
            </motion.div>
            <motion.div className="lg:col-span-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <Card className="p-6 shadow-xl border-border">
                <h2 className="font-['Space_Grotesk'] font-semibold text-lg mb-4">Pourquoi Ptitservices à {city.name} ?</h2>
                <ul className="space-y-3">
                  {[
                    { icon: UserCheck, text: 'Un Easer dédié qui connaît votre domicile et vos habitudes' },
                    { icon: Zap, text: 'Crédit d\'impôt 50% déduit immédiatement (avance immédiate Urssaf)' },
                    { icon: Clock, text: 'Réponse sous 24h ouvrées, rendez-vous d\'évaluation gratuit' },
                    { icon: ShieldCheck, text: `Organisme déclaré Services à la Personne — ${SAP_NUMBER}` },
                  ].map((it) => (
                    <li key={it.text} className="flex items-start gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                        <it.icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                      </div>
                      <span className="leading-relaxed pt-1">{it.text}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pages dédiées ménage / jardinage */}
      <section className="py-10 sm:py-12 -mt-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="city-service-landing-links">
            {[
              { key: 'menage', icon: Home, title: `Femme de ménage à ${city.name}`, desc: 'Entretien du domicile, repassage, vitres, grand ménage ponctuel. Dès 14,99 €/h après crédit d\'impôt.' },
              { key: 'jardinage', icon: Trees, title: `Jardinier à ${city.name}`, desc: 'Tonte, taille de haies, désherbage, ramassage des feuilles. Dès 24,95 €/h après crédit d\'impôt.' },
              { key: 'taille-haies', icon: Scissors, title: `Taille de haies à ${city.name}`, desc: 'Thuyas, lauriers, charmilles : taille d\'entretien, de forme ou réduction de hauteur, déchets ramassés.' },
            ].map((it) => (
              <Link key={it.key} to={serviceCityPath(it.key, city)} data-testid={`city-link-${it.key}`}>
                <Card className="p-5 h-full flex items-start gap-4 border-2 border-[hsl(var(--primary))]/15 hover:border-[hsl(var(--primary))] hover:shadow-md transition-[box-shadow,border-color] group">
                  <div className="w-11 h-11 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0"><it.icon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                  <div className="flex-1">
                    <h2 className="font-['Space_Grotesk'] font-semibold group-hover:text-[hsl(var(--primary))] transition-colors">{it.title}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{it.desc}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-[hsl(var(--primary))] mt-1" />
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Nos services à {city.name}</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Tout ce que votre Easer peut faire pour vous</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Des prestations régulières ou ponctuelles, toutes éligibles au crédit d'impôt de 50%.</p>
          </motion.div>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="city-services-grid">
            {services.map((service) => (
              <motion.div key={service.id} variants={fadeUp}>
                <Link to={`/services/${service.id}`}>
                  <Card className="p-6 h-full border border-border shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-200 group">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3">
                      <service.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-semibold mb-1 group-hover:text-[hsl(var(--primary))] transition-colors">{service.title} à {city.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{service.shortDesc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">à partir de <strong className="text-[hsl(var(--primary))]">{service.priceAfter.toFixed(2).replace('.', ',')} €/h</strong></span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 group-hover:text-[hsl(var(--primary))]">Détails <ArrowRight className="h-3 w-3" /></span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {globals.map((g) => (
              <Card key={g.id} className="p-6 border-2 border-[hsl(var(--primary))]/20 bg-[hsl(var(--brand-mint))]/20">
                <Badge className="mb-3 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]">Global Service</Badge>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg mb-2">{g.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">Tout inclus, à la demande, avec un Easer attitré à {city.name}. À partir de <strong>{g.priceAfter.toFixed(2).replace('.', ',')} €/h</strong> après avance immédiate.</p>
                <Link to={`/services/${g.id}`}><Button variant="outline" size="sm">Découvrir l'offre <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal local content (unique per city & season, AI-written) */}
      <SeasonalLocalContent pageType="general" citySlug={city.slug} cityName={city.name} />

      {/* Simulator */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Simulateur</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Combien coûte une aide à domicile à {city.name} ?</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">Estimez en quelques secondes votre reste à charge réel après le crédit d'impôt de 50%. Le résultat est transférable directement dans votre demande de devis.</p>
              <ul className="space-y-2">
                {['Tarifs « à partir de », transparents et sans surprise', 'Aucune avance de frais grâce à l\'avance immédiate', 'Ponctuel sans engagement ou régulier à tarif préférentiel'].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />{t}</li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <CostSimulator ville={city.name} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">FAQ {city.name}</Badge>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight">Vos questions sur nos services à {city.name}</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="city-faq">
            {faq.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Nearby cities */}
      {nearby.length > 0 && (
        <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold tracking-tight mb-2">Nous intervenons aussi près de {city.name}</h2>
            <p className="text-sm text-muted-foreground mb-5">{city.region} — communes voisines desservies par notre agence de Servins.</p>
            <div className="flex flex-wrap gap-2" data-testid="city-nearby-links">
              {nearby.map((c) => (
                <Link key={c.slug} to={cityPath(c)} className="px-3 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">
                  {c.name}
                </Link>
              ))}
              <Link to="/services-a-domicile" className="px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-sm hover:bg-[hsl(var(--primary))]/90 transition-colors flex items-center gap-1">
                Toutes les villes <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Ptitconseils + CTA */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Sparkles className="h-8 w-8 mx-auto mb-4 opacity-80" />
          <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Facilitez-vous la vie à {city.name} dès aujourd'hui</h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">Devis gratuit, sans engagement, réponse sous 24h ouvrées. Et en attendant, découvrez nos astuces ménage, jardinage et bricolage.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/devis" state={{ ville: city.name, cp: city.cp || '' }}>
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">Demander un devis <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
            <Link to="/ptitconseils">
              <Button size="lg" variant="secondary">Voir les Ptitconseils</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
